import React, { isValidElement, useCallback, useContext, useEffect, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import classnames from 'classnames';
import isString from 'lodash/isString';
import isFunction from 'lodash/isFunction';
import { Size } from 'choerodon-ui/lib/_util/enum';
import ConfigContext from 'choerodon-ui/lib/config-provider/ConfigContext';
import { ProgressStatus } from 'choerodon-ui/lib/progress/enum';
import Progress from '../progress';
import Icon from '../icon';
import Picture from '../picture/Picture';
import Button from '../button/Button';
import { FuncType } from '../button/enum';
import { hide, show } from '../tooltip/singleton';
import { formatFileSize } from './utils';
import Tooltip from '../tooltip/Tooltip';
import { $l } from '../locale-context';
const ATTACHMENT_TARGET = 'attachment-preview';
const Item = function Item(props) {
    const { attachment, listType, prefixCls, onUpload, onRemove, pictureWidth: width, bucketName, onHistory, onPreview, previewTarget = ATTACHMENT_TARGET, bucketDirectory, storageCode, attachmentUUID, isCard, provided, readOnly, restCount, draggable, index, hidden, isPublic, } = props;
    const { status, name, filename, ext, url, size, type } = attachment;
    const { getConfig, getTooltipTheme, getTooltipPlacement } = useContext(ConfigContext);
    const attachmentConfig = getConfig('attachment');
    const tooltipRef = useRef(false);
    const pictureRef = useRef(null);
    const { getPreviewUrl, getDownloadUrl } = attachmentConfig;
    const src = getPreviewUrl ? getPreviewUrl({ attachment, bucketName, bucketDirectory, storageCode, attachmentUUID, isPublic }) : url;
    const downloadUrl = getDownloadUrl && getDownloadUrl({
        attachment,
        bucketName,
        bucketDirectory,
        storageCode,
        attachmentUUID,
        isPublic,
    });
    const dragProps = { ...provided.dragHandleProps };
    const isPicture = type.startsWith('image') || ['png', 'gif', 'jpg', 'webp', 'jpeg', 'bmp', 'tif', 'pic', 'svg'].includes(ext);
    const preview = !!src && (status === 'success' || status === 'done');
    const handlePreview = useCallback(() => {
        const { current } = pictureRef;
        if (current) {
            current.preview();
        }
    }, [pictureRef]);
    const renderDragger = () => {
        if (draggable && !isCard) {
            const iconProps = {
                className: `${prefixCls}-drag-icon`,
                type: 'baseline-drag_indicator',
            };
            if (status !== 'deleting') {
                Object.assign(iconProps, dragProps);
            }
            return (React.createElement(Icon, Object.assign({}, iconProps)));
        }
    };
    const renderImagePreview = () => {
        if (listType === 'text') {
            const { renderIcon } = attachmentConfig;
            const defaultIcon = React.createElement(Icon, { type: "insert_drive_file" });
            const icon = renderIcon ? renderIcon(attachment, listType, defaultIcon) : defaultIcon;
            const isSrcIcon = isString(icon);
            return isPicture || isSrcIcon ? (React.createElement(Picture, { width: 14, height: 14, alt: name, previewUrl: src, downloadUrl: downloadUrl, src: isSrcIcon ? icon : undefined, objectFit: "contain", status: "loaded", index: index, className: `${prefixCls}-icon`, previewTarget: isSrcIcon && !isPicture ? previewTarget : undefined, preview: preview, onPreview: onPreview, ref: pictureRef }, isValidElement(icon) ? icon : undefined)) : preview ? (React.createElement(Button, { href: src, target: previewTarget, funcType: FuncType.link, className: `${prefixCls}-icon` }, icon)) : (React.createElement("div", { className: `${prefixCls}-icon` }, icon));
        }
        if (isCard || listType === 'picture') {
            if ((preview || status === 'deleting') && isPicture) {
                return (React.createElement(Picture, { width: width, height: width, alt: name, src: src || url, downloadUrl: downloadUrl, lazy: true, objectFit: "contain", index: index, preview: preview }));
            }
            return React.createElement(Picture, { width: width, height: width, alt: name, status: status === 'error' ? 'error' : 'empty', index: index });
        }
    };
    const renderPlaceholder = () => {
        if (restCount && isCard) {
            return (React.createElement("div", { className: `${prefixCls}-placeholder` },
                "+",
                restCount));
        }
    };
    const renderTitle = (isCardTitle) => {
        const fileName = (React.createElement(React.Fragment, null,
            React.createElement("span", { className: `${prefixCls}-name` }, filename),
            ext && React.createElement("span", { className: `${prefixCls}-ext` },
                ".",
                ext)));
        const nameNode = preview && listType === 'text' ? (React.createElement("a", Object.assign({}, isPicture ? { onClick: handlePreview } : { href: src, target: previewTarget }, { className: `${prefixCls}-link` }), fileName)) : fileName;
        return (React.createElement("span", { className: `${prefixCls}-title`, style: isCardTitle ? { width } : undefined },
            nameNode,
            !isCardTitle && React.createElement("span", { className: `${prefixCls}-size` },
                " (",
                formatFileSize(size),
                ")")));
    };
    const renderProgress = () => {
        if (status === 'uploading') {
            return (React.createElement(Progress, { value: attachment.percent || 0, size: Size.small, showInfo: false, strokeWidth: 2, className: `${prefixCls}-progress`, status: ProgressStatus.normal }));
        }
    };
    const renderButtons = () => {
        const buttons = [];
        if (attachmentUUID && !readOnly && status === 'error' && !attachment.invalid) {
            const upProps = {
                key: 'upload',
                className: classnames(`${prefixCls}-icon`),
                icon: 'replay',
                onClick: () => onUpload(attachment, attachmentUUID),
                funcType: FuncType.link,
                block: isCard,
            };
            buttons.push(React.createElement(Button, Object.assign({}, upProps)));
        }
        if (!status || status === 'success' || status === 'done') {
            if (attachmentUUID && onHistory) {
                const historyProps = {
                    className: classnames(`${prefixCls}-icon`),
                    icon: 'library_books',
                    onClick: () => onHistory(attachment, attachmentUUID),
                    funcType: FuncType.link,
                    block: isCard,
                };
                buttons.push(React.createElement(Tooltip, { key: "history", title: $l('Attachment', 'view_operation_records') },
                    React.createElement(Button, Object.assign({}, historyProps))));
            }
            if (downloadUrl) {
                const downProps = {
                    className: classnames(`${prefixCls}-icon`),
                    icon: isCard ? 'arrow_downward' : 'get_app',
                    funcType: FuncType.link,
                    href: isString(downloadUrl) ? downloadUrl : undefined,
                    onClick: isFunction(downloadUrl) ? downloadUrl : undefined,
                    target: previewTarget,
                    block: isCard,
                };
                buttons.push(React.createElement(Tooltip, { key: "download", title: $l('Attachment', 'download') },
                    React.createElement(Button, Object.assign({}, downProps))));
            }
        }
        if (attachmentUUID && !readOnly && status !== 'uploading') {
            const rmProps = {
                className: classnames(`${prefixCls}-icon`),
                icon: isCard ? 'delete_forever-o' : 'close',
                onClick: () => onRemove(attachment),
                funcType: FuncType.link,
                block: isCard,
            };
            buttons.push(React.createElement(Tooltip, { key: "remove", title: status === 'deleting' ? undefined : $l('Attachment', 'delete') },
                React.createElement(Button, Object.assign({}, rmProps))));
        }
        if (buttons.length) {
            return (React.createElement("div", { className: classnames(`${prefixCls}-buttons`, { [`${prefixCls}-buttons-visible`]: status === 'deleting' }) }, buttons));
        }
    };
    const renderErrorMessage = () => {
        if (status === 'error') {
            const { errorMessage } = attachment;
            if (errorMessage) {
                return (React.createElement("div", { className: `${prefixCls}-error-content` },
                    React.createElement(Icon, { type: "warning" }),
                    React.createElement("span", { className: `${prefixCls}-error-message` }, errorMessage)));
            }
        }
    };
    const errorMessageNode = renderErrorMessage();
    const handleMouseEnter = useCallback((e) => {
        if (errorMessageNode) {
            show(e.currentTarget, {
                title: errorMessageNode,
                theme: getTooltipTheme('validation'),
                placement: getTooltipPlacement('validation') || 'bottomLeft',
            });
            tooltipRef.current = true;
        }
    }, [errorMessageNode, getTooltipTheme, getTooltipPlacement, tooltipRef]);
    const handleMouseLeave = useCallback(() => {
        if (tooltipRef.current) {
            hide();
            tooltipRef.current = false;
        }
    }, [tooltipRef]);
    useEffect(() => () => {
        if (tooltipRef.current) {
            hide();
            tooltipRef.current = false;
        }
    }, []);
    const listProps = {
        ref: provided.innerRef,
        className: classnames(prefixCls, {
            [`${prefixCls}-error`]: status === 'error',
            [`${prefixCls}-success`]: status === 'success',
        }),
        ...provided.draggableProps,
        style: {
            ...provided.draggableProps.style,
        },
    };
    if (draggable && isCard) {
        Object.assign(listProps, dragProps);
    }
    return (React.createElement("div", Object.assign({}, listProps, { hidden: hidden }),
        React.createElement("div", { className: `${prefixCls}-container`, onMouseEnter: isCard ? handleMouseEnter : undefined, onMouseLeave: isCard ? handleMouseLeave : undefined },
            React.createElement("div", { className: `${prefixCls}-content` },
                renderDragger(),
                renderImagePreview(),
                renderPlaceholder(),
                !restCount && !isCard && renderTitle(),
                !restCount && renderButtons()),
            errorMessageNode,
            renderProgress()),
        !restCount && isCard && renderTitle(true)));
};
Item.displayName = 'Item';
export default observer(Item);
